import React from "react";

import { FaEnvelope, FaLock, FaUser, FaStore } from "react-icons/fa";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// Helper to translate technical errors to user-friendly messages
function getFriendlyErrorMessage(errString) {
    if (!errString) return "An unexpected error occurred. Please try again.";

    const lowerMsg = String(errString).toLowerCase();

    if (lowerMsg.includes('usernameexistsexception')) {
        return "An account with this username already exists.";
    }
    if (lowerMsg.includes('invalidpasswordexception')) {
        return "Your password does not meet the security requirements (e.g., minimum length, numbers, special characters).";
    }
    if (lowerMsg.includes('invalidparameterexception')) {
        return "Please check your information. Your email or username might be in an invalid format.";
    }
    if (lowerMsg.includes('limitexceededexception') || lowerMsg.includes('toomanyrequestsexception')) {
        return "Too many attempts. Please try again later.";
    }
    if (lowerMsg.includes('network error')) {
        return "Network error. Please check your internet connection.";
    }
    // Fallback for any other technical string
    if (lowerMsg.includes('exception')) {
        return "Something went wrong during registration. Please try again.";
    }

    // If it doesn't look like a technical exception, just return it
    return String(errString);
}

function RegisterForm() {
    const location = useLocation();
    const [username, setUsername] = useState(location.state?.username || '');
    const [password, setPassword] = useState(location.state?.password || '');
    const [email, setEmail] = useState(location.state?.email || '');
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [businessID, setBusinessID] = useState(location.state?.businessID || '');
    const [accountType, setAccountType] = useState(location.state?.accountType || 'customer'); // default
    const [acceptedLegal, setAcceptedLegal] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const API_BASE_URL = process.env.REACT_APP_API_BASE;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const incomingBusinessID =
            params.get('business_id') ||
            params.get('busiess_id') ||
            params.get('business') ||
            location.state?.businessID ||
            '';
        console.log("business id:", incomingBusinessID)
        setBusinessID(incomingBusinessID);
    }, [location.search, location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); // clear any previous error

        if (!acceptedLegal) {
            setError("You must agree to the Terms of Service and acknowledge the Privacy Policy to register.");
            return;
        }

        if (!API_BASE_URL) {
            setError("Missing API base URL configuration.");
            return;
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/register`,
                {
                    username,
                    password,
                    email,
                    accountType,
                    acceptedLegal: true,
                    acceptedLegalAt: new Date().toISOString(),
                },
                {
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("Success:", response.data);

            navigate("/confirm-signup", {
                state: {
                    userId: response.data.user_id,
                    username,
                    email,
                    password,
                    accountType,
                    businessID,
                },
            });

        } catch (err) {
            console.error('Register error:', err);

            let message = 'Registration failed';

            // If backend sends something in response.data
            if (err.response && err.response.data) {
                const data = err.response.data;
                console.log("err.response.data: ", data)

                message = data.error
            } else if (err.message) {
                // Generic JS / Axios error message
                message = err.message;
            }

            if (message === "username already in use" && location.state?.fromConfirm) {
                console.log("Attempting to delete unconfirmed user to allow email change...");
                try {
                    await axios.post(
                        `${API_BASE_URL}/delete-unconfirmed`,
                        { username },
                        { headers: { "Content-Type": "application/json" } }
                    );
                    // Successfully deleted unconfirmed account! 
                    // Let's clear the fromConfirm flag so it doesn't loop infinitely, then retry registration.
                    navigate(location.pathname, { replace: true, state: { ...location.state, fromConfirm: false } });
                    return handleSubmit(e);
                } catch (delErr) {
                    console.error("Failed to delete unconfirmed user:", delErr);
                    // If deletion failed (e.g. they are fully confirmed), just fall through to the normal error
                }
            }

            setError(getFriendlyErrorMessage(message));
        }
    };

    return (
        <div className="wrapper">
            <form onSubmit={handleSubmit}>
                <h1>Lowk</h1>
                <h2>Registration</h2>

                {/* account type selection */}
                <div className="account-type">
                    <p>What type of account are you registering?</p>
                    <div className="account-type-boxes">
                        <label className={`type-box ${accountType === 'customer' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="accountType"
                                value="customer"
                                checked={accountType === 'customer'}
                                onChange={(e) => setAccountType(e.target.value)}
                            />
                            <FaUser className="box-icon" />
                            <span>Customer</span>
                        </label>

                        <label className={`type-box ${accountType === 'business' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="accountType"
                                value="business"
                                checked={accountType === 'business'}
                                onChange={(e) => setAccountType(e.target.value)}
                            />
                            <FaStore className="box-icon" />
                            <span>Business</span>
                        </label>
                    </div>
                </div>

                {/* email */}
                <div className="input-box">
                    <input
                        type="email"
                        placeholder="example@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <FaEnvelope className="icon" />
                </div>

                {/* username */}
                <div className="input-box">
                    <input
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <FaUser className="icon" />
                </div>

                {/* password */}
                <div className="input-box">
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={() => setPasswordFocused(false)}
                        required
                    />
                    <FaLock className="icon" />
                </div>

                <div className={`password-requirements ${passwordFocused || password ? 'visible' : ''}`}>
                    <p className="req-title">Password must contain:</p>
                    <ul className="req-list">
                        <li className={password.length >= 8 ? 'valid' : 'invalid'}>At least 8 characters long</li>
                        <li className={/[a-z]/.test(password) ? 'valid' : 'invalid'}>One lowercase letter</li>
                        <li className={/[A-Z]/.test(password) ? 'valid' : 'invalid'}>One uppercase letter</li>
                        <li className={/[0-9]/.test(password) ? 'valid' : 'invalid'}>One number</li>
                        <li className={/[^a-zA-Z0-9]/.test(password) ? 'valid' : 'invalid'}>One symbol</li>
                    </ul>
                </div>

                <div className="legal-consent">
                    <label className="legal-row">
                        <input
                            type="checkbox"
                            checked={acceptedLegal}
                            onChange={(e) => setAcceptedLegal(e.target.checked)}
                            required
                        />
                        <span>
                            I agree to the{" "}
                            <Link to="/tos" target="_blank" rel="noreferrer">
                                Terms of Service
                            </Link>{" "}
                            and acknowledge the{" "}
                            <Link to="/privacy" target="_blank" rel="noreferrer">
                                Privacy Policy
                            </Link>
                            .
                        </span>
                    </label>
                </div>

                {error && (
                    <p className="error">
                        {typeof error === 'string' ? error : JSON.stringify(error)}
                    </p>
                )}

                <button type="submit">Register</button>

                <div className="register-link">
                    <p>Already have an account? <a href="/">Login</a></p>
                </div>
            </form>
        </div>
    )
}

export default RegisterForm;
